import React, { useState } from 'react';
import './BetaFeedbackPage.css';
import { saveBetaFeedback } from '../utils/db';

// ── Screen definitions ────────────────────────────────────────────────────────
// textLabel: null = auto-advance on tap (no text input shown)
// textLabel: string = text input slides in after selection; Continue button shown
// options: null = text-only screen (open)
const SCREENS = [
  {
    id:        'scan',
    question:  'Did your receipt scan correctly?',
    options:   [
      { value: 'yes_mostly',   label: '👍 Yes, mostly right' },
      { value: 'some_issues',  label: '😐 Some issues'       },
      { value: 'not_really',   label: '👎 Not really'         },
    ],
    textLabel: "Anything that didn't match?",
  },
  {
    id:        'clarity',
    question:  'Did the comparison make sense?',
    options:   [
      { value: 'yes_clear',  label: '👍 Yes, clear' },
      { value: 'mostly',     label: '😐 Mostly'     },
      { value: 'confusing',  label: '👎 Confusing'   },
    ],
    textLabel: "What didn't make sense?",
  },
  {
    id:        'value',
    question:  'Would you use this again?',
    options:   [
      { value: 'yes',    label: 'Yes'   },
      { value: 'maybe',  label: 'Maybe' },
      { value: 'no',     label: 'No'    },
    ],
    textLabel: null,   // auto-advance — no text input
  },
  {
    id:        'open',
    question:  'Anything else?',
    options:   null,   // text-only screen
    textLabel: 'A bug, something missing, something that worked well…',
  },
];

// ─────────────────────────────────────────────────────────────────────────────

function BetaFeedbackPage({ onBack }) {
  const [screenIndex, setScreenIndex] = useState(0);
  const [answers,     setAnswers]     = useState({});  // { scan: 'yes_mostly', … }
  const [texts,       setTexts]       = useState({});  // { scan: 'optional note', … }
  const [advancing,   setAdvancing]   = useState(false);
  const [done,        setDone]        = useState(false);
  const [submitting,  setSubmitting]  = useState(false);

  const screen    = SCREENS[screenIndex];
  const selected  = answers[screen?.id];
  const isAutoAdv = screen?.textLabel === null;   // value screen — auto-advance
  const isOpen    = screen?.id === 'open';        // text-only final screen
  const showText  = selected && screen?.textLabel && !isAutoAdv;

  // ── Option tap ──────────────────────────────────────────────────────────────
  const handleOption = (screenId, value) => {
    if (advancing) return;
    setAnswers(prev => ({ ...prev, [screenId]: value }));

    if (isAutoAdv) {
      setAdvancing(true);
      setTimeout(() => {
        setScreenIndex(i => i + 1);
        setAdvancing(false);
      }, 400);
    }
    // Screens with text wait for explicit "Continue →" tap
  };

  // ── Continue (screens with optional text) ──────────────────────────────────
  const handleContinue = () => {
    if (advancing || !selected) return;
    setAdvancing(true);
    setTimeout(() => {
      setScreenIndex(i => i + 1);
      setAdvancing(false);
    }, 60);
  };

  // ── Final submit / skip ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await saveBetaFeedback('beta_stepper', {
        scanQuality:     answers.scan    || null,
        scanQualityText: texts.scan      || null,
        clarity:         answers.clarity || null,
        clarityText:     texts.clarity   || null,
        returnIntent:    answers.value   || null,
        openText:        texts.open      || null,
      });
    } catch { /* silent — don't block UX */ }
    setDone(true);
    setSubmitting(false);
  };

  // ── Done screen ────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="bf-page">
        <div className="bf-done">
          <div className="bf-done-check">✓</div>
          <p className="bf-done-heading">Got it — thank you.</p>
          <p className="bf-done-sub">This helps us improve your experience.</p>
          <p className="bf-done-sig">— Yas</p>
          <button className="bf-done-cta" onClick={onBack}>Done</button>
        </div>
      </div>
    );
  }

  // ── Stepper ────────────────────────────────────────────────────────────────
  return (
    <div className="bf-page">

      {/* Progress dots */}
      <div className="bf-dots" aria-hidden="true">
        {SCREENS.map((s, i) => (
          <span
            key={s.id}
            className={[
              'bf-dot',
              i === screenIndex ? 'bf-dot--active' : '',
              i < screenIndex   ? 'bf-dot--done'   : '',
            ].filter(Boolean).join(' ')}
          />
        ))}
      </div>

      {/* Screen content */}
      <div className={`bf-screen${advancing ? ' bf-screen--fade' : ''}`}>

        <p className="bf-question">{screen.question}</p>

        {/* Option buttons */}
        {screen.options && (
          <div className="bf-options">
            {screen.options.map(opt => (
              <button
                key={opt.value}
                className={`bf-opt${selected === opt.value ? ' bf-opt--sel' : ''}`}
                onClick={() => handleOption(screen.id, opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Optional text — appears after option selected (non-auto-advance screens) */}
        {showText && (
          <textarea
            className="bf-textarea"
            placeholder={screen.textLabel}
            value={texts[screen.id] || ''}
            onChange={e => setTexts(prev => ({ ...prev, [screen.id]: e.target.value }))}
            rows={3}
          />
        )}

        {/* Continue button (non-auto-advance, option-based screens) */}
        {showText && (
          <button className="bf-continue" onClick={handleContinue}>
            Continue →
          </button>
        )}

        {/* Skip — available on option screens before the final open screen */}
        {!isOpen && !isAutoAdv && !showText && screenIndex < SCREENS.length - 1 && (
          <button className="bf-skip" onClick={() => {
            setAdvancing(true);
            setTimeout(() => {
              setScreenIndex(i => i + 1);
              setAdvancing(false);
            }, 60);
          }}>
            Skip
          </button>
        )}

        {/* Open (text-only) screen */}
        {isOpen && (
          <>
            <textarea
              className="bf-textarea"
              placeholder={screen.textLabel}
              value={texts.open || ''}
              onChange={e => setTexts(prev => ({ ...prev, open: e.target.value }))}
              rows={4}
            />
            <button
              className="bf-submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Send feedback'}
            </button>
            {!submitting && (
              <button className="bf-skip" onClick={handleSubmit}>
                Skip
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default BetaFeedbackPage;
